/**
 * ChannelVideoPicker — pick a video to edit from the connected channel's own
 * uploads, rendered with the shared ProviderPicker card grid (same component
 * the LLM/provider pickers use) instead of a bespoke grid.
 *
 * Selecting a card calls onSelect(video); the parent form fills the URL field
 * (and title/description/source language) from the selection. The raw URL field
 * stays available as a fallback, so this is additive.
 *
 * Search: a debounced box drives SERVER-SIDE search (search.list) for queries
 * >= 3 chars (gated — 100 quota units); shorter queries just filter the already
 * loaded uploads client-side (free). Pagination is button-based ("Load more").
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { listChannelVideos, listChannels } from '../../services/youtubePublishApi';
import type { YouTubeChannelVideo } from '../../services/youtubePublishApi';
import { HttpResponseError } from '../../utils/httpUtils';
import { ProviderPicker } from '../common/ProviderPicker';
import type { PickerOption } from '../common/ProviderPicker';

interface ChannelVideoPickerProps {
  channel_id: string;
  selectedVideoId: string | null;
  onSelect: (video: YouTubeChannelVideo) => void;
}

const SEARCH_MIN_LEN = 3;
const SEARCH_DEBOUNCE_MS = 400;

/** i18n key describing why the video list could not be loaded. */
function errorKeyFor(err: unknown): string {
  if (err instanceof HttpResponseError) {
    if (err.status === 401) return 'youtube.publish.picker.sessionExpired';
    if (err.status === 429) return 'youtube.publish.picker.quotaExceeded';
  }
  return 'youtube.publish.picker.error';
}

/** Format a duration in seconds as mm:ss or h:mm:ss. */
function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

export function ChannelVideoPicker({
  channel_id,
  selectedVideoId,
  onSelect,
}: ChannelVideoPickerProps) {
  const { t } = useTranslation();

  const [videos, setVideos] = useState<YouTubeChannelVideo[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedOnce, setLoadedOnce] = useState(false);

  // Channel avatar + name header (reuses the existing /channels endpoint).
  const [channelMeta, setChannelMeta] = useState<{ title: string; thumbnail_url: string } | null>(
    null
  );

  // Search: immediate `query` drives client-filter; `debouncedQuery` triggers
  // the (gated) server-side search.
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const loadPage = useCallback(
    async (
      pageToken: string | null,
      append: boolean,
      serverQuery: string | null
    ): Promise<void> => {
      if (!channel_id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await listChannelVideos(channel_id, pageToken, serverQuery);
        setVideos((prev) => (append ? [...prev, ...data.videos] : data.videos));
        setNextPageToken(data.next_page_token);
      } catch (err) {
        // Never render the backend's prose: a 401 here used to print the raw
        // OAuth diagnostic (channel ID + Google's invalid_grant payload) into
        // the panel. Branch on the CONDITION and show a translated message.
        setError(errorKeyFor(err));
      } finally {
        setLoading(false);
        setLoadedOnce(true);
      }
    },
    [channel_id]
  );

  // Whether the current debounced query hits the server-side search path.
  const serverQuery = debouncedQuery.length >= SEARCH_MIN_LEN ? debouncedQuery : null;

  // (Re)load when the channel changes or the debounced query crosses the gate.
  useEffect(() => {
    setVideos([]);
    setNextPageToken(null);
    setLoadedOnce(false);
    void loadPage(null, false, serverQuery);
  }, [loadPage, serverQuery]);

  // Channel avatar header.
  useEffect(() => {
    if (!channel_id) return;
    let cancelled = false;
    listChannels(channel_id)
      .then((channels) => {
        if (cancelled) return;
        const ch = channels.find((c) => c.id === channel_id) ?? channels[0];
        if (ch) setChannelMeta({ title: ch.title, thumbnail_url: ch.thumbnail_url });
      })
      .catch(() => {
        // Non-fatal: the picker still works without the avatar header.
      });
    return () => {
      cancelled = true;
    };
  }, [channel_id]);

  // For 1-2 char queries we filter the already-loaded uploads client-side (free).
  const displayVideos = useMemo(() => {
    if (query && query.length > 0 && query.length < SEARCH_MIN_LEN) {
      const q = query.toLowerCase();
      return videos.filter((v) => (v.title || '').toLowerCase().includes(q));
    }
    return videos;
  }, [videos, query]);

  const options = useMemo<PickerOption<YouTubeChannelVideo>[]>(
    () =>
      displayVideos.map((v) => ({
        value: v.video_id,
        label: v.title || v.video_id,
        iconUrl: v.thumbnail_url,
        meta: v,
      })),
    [displayVideos]
  );

  const handleChange = useCallback(
    (value: string) => {
      const video = videos.find((v) => v.video_id === value);
      if (video) onSelect(video);
    },
    [videos, onSelect]
  );

  const renderCard = useCallback(
    (opt: PickerOption<YouTubeChannelVideo>, isSelected: boolean, onClick: () => void) => {
      const video = opt.meta as YouTubeChannelVideo;
      return (
        <button
          type="button"
          onClick={onClick}
          className={`group flex w-full flex-col text-left rounded-lg overflow-hidden border transition-colors ${
            isSelected
              ? 'border-purple-500 ring-2 ring-purple-500/50 bg-purple-900/20'
              : 'border-white/10 bg-black/30 hover:border-white/30'
          }`}
        >
          {/* shrink-0: ProviderPicker's grid uses `auto-rows-fr`
              (grid-auto-rows: minmax(0,1fr)) and stretches each flex-column card
              to the row height. Without shrink-0 this aspect-ratio box — whose
              only child is an absolutely-positioned img, so it has zero
              intrinsic height — gets shrunk to ~0 and the thumbnail vanishes in
              the grid (it still showed in the collapsed selected row, which uses
              a fixed-size img). shrink-0 makes it keep its 16:9 height. */}
          <div className="relative aspect-video w-full shrink-0 bg-black/40">
            {video.thumbnail_url ? (
              <img
                src={video.thumbnail_url}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : null}
            {video.duration_seconds > 0 && (
              <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                {formatDuration(video.duration_seconds)}
              </span>
            )}
            {isSelected && (
              <span className="absolute top-1 right-1 bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                {t('youtube.publish.picker.selected')}
              </span>
            )}
          </div>
          <span className="text-white/80 text-xs leading-snug p-2 line-clamp-2">
            {video.title || video.video_id}
          </span>
        </button>
      );
    },
    [t]
  );

  // Search box + "Load more" live INSIDE the picker toolbar (extraFilters) so
  // interacting with them does not collapse the expanded grid.
  const extraFilters = (
    <div className="flex items-center gap-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('youtube.publish.picker.searchPlaceholder')}
          className="w-40 sm:w-56 min-h-[44px] pl-8 pr-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
        />
      </div>
      {nextPageToken && (
        <button
          type="button"
          onClick={() => void loadPage(nextPageToken, true, serverQuery)}
          disabled={loading}
          className="min-h-[44px] px-3 py-2 rounded-lg bg-black/30 hover:bg-black/40 disabled:opacity-50 disabled:cursor-not-allowed text-white/80 text-xs whitespace-nowrap transition-colors"
        >
          {loading ? t('common.loading') : t('youtube.publish.picker.loadMore')}
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-3 p-4 bg-black/20 rounded-lg border border-white/10">
      {/* Channel avatar + name header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {channelMeta?.thumbnail_url && (
            <img
              src={channelMeta.thumbnail_url}
              alt=""
              className="h-8 w-8 rounded-full object-cover shrink-0"
            />
          )}
          <div className="min-w-0">
            <p className="text-white/80 text-sm font-medium truncate">
              {channelMeta?.title || t('youtube.publish.picker.title')}
            </p>
            {channelMeta && (
              <p className="text-white/40 text-xs">{t('youtube.publish.picker.title')}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setQuery('');
            void loadPage(null, false, null);
          }}
          disabled={loading}
          className="flex items-center gap-1 min-h-[44px] px-2 text-white/50 hover:text-white disabled:opacity-50 transition-colors text-xs shrink-0"
          aria-label={t('youtube.publish.picker.refresh')}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <p className="text-red-400 text-xs" role="alert">
          {t(error)}
        </p>
      )}

      {!error && loadedOnce && displayVideos.length === 0 && !loading && (
        <p className="text-white/40 text-xs">{t('youtube.publish.picker.empty')}</p>
      )}

      <ProviderPicker<YouTubeChannelVideo>
        options={options}
        value={selectedVideoId}
        onChange={handleChange}
        defaultExpanded
        searchable={false}
        sortKeys={[]}
        extraFilters={extraFilters}
        renderCard={renderCard}
        cardMinWidth={180}
        emptyState={
          <p className="text-center text-white/40 py-6 text-sm">
            {loading ? t('common.loading') : t('youtube.publish.picker.empty')}
          </p>
        }
      />
    </div>
  );
}
